import React, { useMemo } from "react"
import { Table, Space, Typography, Button, Tag, Dropdown } from "@mimber/ui-web"
import type { ColumnsType } from "@mimber/ui-web"
import {
  ClockCircleOutlined,
  MoreOutlined,
  DeleteOutlined,
  TeamOutlined,
} from "@mimber/ui-web"
import { formatTableDate } from "@mimber/ui-web"
import type {
  RoleAssignmentWithRelationsDto,
  GroupRoleAssignmentWithRelationsDto,
} from "@mimber/schema"
import type { MemberRoleAssignmentsTableProps } from "./MemberRoleAssignmentsTable.types"

const { Text } = Typography

type AssignmentRow =
  | RoleAssignmentWithRelationsDto
  | GroupRoleAssignmentWithRelationsDto

export const MemberRoleAssignmentsTable: React.FunctionComponent<
  MemberRoleAssignmentsTableProps
> = ({ assignments, isLoading, source, onRevoke, className }) => {
  const columns: ColumnsType<AssignmentRow> = useMemo(() => {
    const baseColumns: ColumnsType<AssignmentRow> = [
      {
        title: "Role",
        key: "roleName",
        render: (_, record) => (
          <Text strong>{record.organizationRole.name}</Text>
        ),
      },
    ]

    if (source === "group") {
      baseColumns.push({
        title: "Group",
        key: "groupName",
        render: (_, record) => {
          const groupRecord = record as GroupRoleAssignmentWithRelationsDto
          return (
            <Space>
              <TeamOutlined />
              <Text>{groupRecord.group.name}</Text>
            </Space>
          )
        },
      })
    }

    baseColumns.push(
      {
        title: "Expires",
        key: "expiresAt",
        render: (_, record) => {
          if (!record.expiresAt) return <Text type="secondary">Never</Text>
          const isExpired = new Date(record.expiresAt) < new Date()
          return (
            <Space>
              <ClockCircleOutlined />
              <Text type={isExpired ? "danger" : undefined}>
                {formatTableDate(record.expiresAt)}
              </Text>
              {isExpired && <Tag color="red">Expired</Tag>}
            </Space>
          )
        },
      },
      {
        title: "Assigned",
        key: "createdAt",
        render: (_, record) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(record.createdAt)}</Text>
          </Space>
        ),
      }
    )

    if (source === "direct" && onRevoke) {
      baseColumns.push({
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_, record) => {
          const directRecord = record as RoleAssignmentWithRelationsDto
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "revoke",
                      label: "Revoke",
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () =>
                        onRevoke(
                          directRecord.uuid,
                          directRecord.organizationRole.uuid
                        ),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <Button type="text" icon={<MoreOutlined />} />
              </Dropdown>
            </div>
          )
        },
      })
    }

    return baseColumns
  }, [source, onRevoke])

  return (
    <Table<AssignmentRow>
      bordered
      columns={columns}
      dataSource={assignments as AssignmentRow[]}
      loading={isLoading}
      rowKey="uuid"
      pagination={false}
      className={className}
      locale={{
        emptyText:
          source === "direct"
            ? "No direct role assignments"
            : "No group-inherited roles",
      }}
    />
  )
}
